import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {ClickableDiv} from '@Components/clickable-div';
import {SkeletonBrick} from '@Components/skeleton-brick';
import type {PosterPagesCarouselItemProps} from './poster-pages-carousel-item.types';
import styles from './poster-pages-carousel-item.module.scss';
import {noop} from '@Utils/general.util';
import {useIsMobileWidth} from '@Hooks/useIsMobileWidth';
import {Text, TextSize} from '@Components/text';

export function PosterPagesCarouselItem({
  pageIndex,
  previewThumbnailUrl,
  previewUrl,
  isSelected,
  isVideo,
  isLoading = false,
  subtitle,
  pageHashedId,
  onItemClick = noop,
}: PosterPagesCarouselItemProps): ReactElement {
  const [isImageLoading, setIsImageLoading] = useState<boolean>(true);
  const isMobile = useIsMobileWidth();

  const handleClick = (): void => {
    onItemClick(pageIndex, previewUrl, pageHashedId);
  };

  const handleImageLoad = (): void => {
    setIsImageLoading(false);
  };

  const handleImageError = (): void => {
    setIsImageLoading(false);
  };

  const getSelectedSubtitleClass = (): string => {
    if (isSelected) {
      return styles.selectedSubtitle;
    }
    return '';
  };

  const renderThumbnailSubtitle = (): ReactElement | null => {
    if (!subtitle) {
      return null;
    }

    return <Text className={`content-body ${getSelectedSubtitleClass()}`} val={subtitle} size={TextSize.SM} bold={isSelected} />;
  };

  const showSkeleton = isLoading || isImageLoading;

  return (
    <div className="flex-column-align-center gap-1">
      <ClickableDiv className={`${styles.carouselItem} ${isMobile ? styles.small : ''} ${isSelected ? styles.selected : ''} radius-8 _position-relative`} onClick={handleClick}>
        {showSkeleton ? <SkeletonBrick className={`${styles.skeleton} radius-8 _full-width _full-height _position-absolute`} /> : null}
        <img
          src={previewThumbnailUrl}
          alt={`Page ${pageIndex + 1} thumbnail`}
          className={`_full-height _full-width ${styles.thumbnail} ${showSkeleton ? styles.hidden : ''}`}
          onLoad={handleImageLoad}
          onError={handleImageError}
        />
        {isVideo && !showSkeleton ? (
          <div className={`${styles.videoIndicator} flexbox radius-2 spacing-p-1 _position-absolute`}>
            <Icon icon="icon-play-video" size={IconSize.SIZE_ICON_12} className="content-body-white" />
          </div>
        ) : null}
      </ClickableDiv>
      {renderThumbnailSubtitle()}
    </div>
  );
}
